import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { CampaignContext } from '../../../../contexts/CampaignContext'
import BackButton from './SIdebarParts/BackButton'
import SectionTitle from './SIdebarParts/SectionTitle'
import SidebarLinks from './SIdebarParts/SidebarLinks'
import { People, BarChartSteps, FileSpreadsheet, GraphUp, House, Images, PencilSquare, FileBarGraph, ChevronLeft, ChevronRight, ClipboardCheck, Archive } from 'react-bootstrap-icons'
import { observer } from 'mobx-react-lite'
import { useSidebarCollapse } from '../../../../contexts/SidebarCollapseContext'
import ArchivedCampaignNotice from './ArchivedCampaignNotice'
import ArchiveModal from '../../Campaigns/ArchiveModal'

export default observer(function CampaignSidebar() {
  let location = useLocation()
  const [activePath, setActivePath] = useState("")
  const [archiveModalOpen, setArchiveModalOpen] = useState(false)
  const { campaignScenarios, selectedScenario, campaign, isArchived, archiveCampaign } = useContext(CampaignContext)
  const { id } = useParams()
  const { isCollapsed, toggleCollapse } = useSidebarCollapse()

  const paths = {
    overview: 'overview',
    files: 'files',
    analytics: 'analytics',
    influencers: 'influencers',
    project_timeline: 'project_timeline',
    draft_process: 'draft_process',
    content_drafts: 'content_drafts',
    scenario_timeline: 'scenario_timeline',
    budget_table_new: 'budget_table_new',
    budget_table: 'budget_table',
    report: 'report',
  }

  const updateSelectedPath = () => {
    const currentPath = Object.keys(paths).find(path => location.pathname.includes(path))
    setActivePath(paths[currentPath])
  }

  useEffect(() => updateSelectedPath(), [location.pathname])

  const budgetTableLink = `/project_manager/campaigns/${id}/budget_table`
  const budgetTableNewLink = `/project_manager/campaigns/${id}/budget_table_new`
  const projectTimelineLink = `/project_manager/campaigns/${id}/project_timeline`

  return (
    <div className={`fixed flex flex-col ${isCollapsed ? 'w-16' : 'w-56'} bg-white dark:bg-rb-dark-mode border-r-1 dark:border-rb-dark-mode-lighter pt-16 h-full transition-all duration-300`}>
      {!isCollapsed && (
        <div className="px-4 flex items-center justify-between">
          <p className="text-xs text-gray-300 dark:text-gray-400 uppercase">Campaign details</p>
          <button
            onClick={toggleCollapse}
            className="p-1 rounded-md hover:bg-gray-200 dark:hover:bg-rb-dark-mode-lighter dark:text-white"
            title="Collapse sidebar"
          >
            <ChevronLeft className="text-sm" />
          </button>
        </div>
      )}
      {isCollapsed && (
        <div className="px-2 flex items-center justify-center py-2">
          <button
            onClick={toggleCollapse}
            className="p-1 rounded-md hover:bg-gray-200 dark:hover:bg-rb-dark-mode-lighter dark:text-white"
            title="Expand sidebar"
          >
            <ChevronRight className="text-sm" />
          </button>
        </div>
      )}
      <BackButton />
      <div className="mb-4" />
      {!isCollapsed && (
        <div className='flex items-center gap-2 p-3 px-4'>
          {campaign.image ? (
            <img src={campaign.image} className='h-11 w-11 rounded-lg object-cover flex-shrink-0' alt={campaign.name} />
          ) : (
            <span className='h-11 w-11 rounded-lg text-sm flex items-center justify-center flex-shrink-0' style={{ backgroundColor: campaign.theme_color }}>
              <span className='text-white'>{campaign.name.slice(0, 2).toUpperCase()}</span>
            </span>
          )}
          <div className='min-w-0 flex-1'>
            <p className='text-sm text-rb-lighter-grey dark:text-gray-400 truncate'>{campaign.client_name}</p>
            <p className='text-md font-bold dark:text-white truncate'>{campaign.name}</p>
          </div>
        </div>
      )}
      {isCollapsed && (
        <div className='flex items-center justify-center p-3'>
          {campaign.image ? (
            <img src={campaign.image} className='h-11 w-11 rounded-lg object-cover flex-shrink-0' alt={campaign.name} />
          ) : (
            <span className='h-11 w-11 rounded-lg text-sm flex items-center justify-center flex-shrink-0' style={{ backgroundColor: campaign.theme_color }}>
              <span className='text-white'>{campaign.name.slice(0, 2).toUpperCase()}</span>
            </span>
          )}
        </div>
      )}
      <ArchivedCampaignNotice isCollapsed={isCollapsed} />
      <div className="mb-8" />
      <SectionTitle text="Overview" />
      <SidebarLinks text="Overview" icon={<House/>} link={`/project_manager/campaigns/${id}/overview`} active={activePath === "overview"} />
      <SidebarLinks text="Files" icon={<Images/>} link={`/project_manager/campaigns/${id}/files`} active={activePath === "files"} />
      <SidebarLinks text="Analytics" icon={<GraphUp/>} link={`/project_manager/campaigns/${id}/analytics`} active={activePath === "analytics"} />
      <div className="mb-8" />
      <SectionTitle text="Campaign flow" />
      <SidebarLinks text="Influencers" icon={<People />} link={`/project_manager/campaigns/${id}/influencers`} active={activePath === "influencers"} />
      <SidebarLinks text="Budget table" icon={<FileSpreadsheet/>} link={budgetTableLink} active={activePath === "budget_table"} />
      <SidebarLinks text="Budget table (new)" icon={<FileSpreadsheet/>} link={budgetTableNewLink} active={activePath === "budget_table_new"} />
      <SidebarLinks text="Project timeline" icon={<BarChartSteps/>} link={projectTimelineLink} active={activePath === "project_timeline"} />
      <SidebarLinks text="Drafts & Insights" icon={<PencilSquare/>} link={`/project_manager/campaigns/${id}/draft_process`} active={activePath === "draft_process"} />
      <SidebarLinks text="Drafts (new)" icon={<ClipboardCheck/>} link={`/project_manager/campaigns/${id}/content_drafts`} active={activePath === "content_drafts"} />
      {selectedScenario && <SidebarLinks text="Report" icon={<FileBarGraph/>} link={`/project_manager/campaigns/${id}/report`} active={activePath === "report"} />}
      {campaign.id && !isArchived && (
        <div className="mt-auto mb-4">
          <SidebarLinks text="Archive campaign" icon={<Archive />} onClick={() => setArchiveModalOpen(true)} />
        </div>
      )}
      <ArchiveModal
        isOpen={archiveModalOpen}
        onClose={() => setArchiveModalOpen(false)}
        name={campaign.name}
        onConfirm={archiveCampaign}
      />
    </div>
  )
})
